import styles from './S3Connection.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { Key, Lock, Link as LinkIcon } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';

const s3Schema = z.object({
  name: z.string().optional().nullable(),
  environment: z.enum(['test', 'prod', 'dev']).optional(),
  accessKeyId: z.string().min(1, 'Access Key ID is required'),
  secretAccessKey: z.string().min(1, 'Secret Access Key is required'),
  bucketName: z.string().min(1, 'Bucket name is required'),
  region: z.string().min(1, 'Region is required'),
  sessionToken: z.string().optional().nullable(),
  endpointUrl: z.string().url('Invalid URL').optional().nullable().or(z.literal('')),
  prefix: z.string().optional().nullable(),
  testPath: z.string().optional().nullable(),
});

type S3Form = z.infer<typeof s3Schema>;

export default function S3Connection() {
  const navigate = useNavigate();
  const goBack = useBackNavigation('/connectors');
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<any | null>(null);
  const [testing, setTesting] = useState(false);
  const user = useAuthStore(state => state.user)
  const { connectors, updateConnection } = useConnectionsStore(state => state);
  const { configId } = useParams();

  const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<S3Form>({
    resolver: zodResolver(s3Schema),
    defaultValues: {
      name: '',
      environment: 'prod' as 'test' | 'prod' | 'dev',
      accessKeyId: '',
      secretAccessKey: '',
      bucketName: '',
      region: '',
      sessionToken: '',
      endpointUrl: '',
      prefix: '',
      testPath: '',
    }
  });
  
  useEffect(() => {
    if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
      navigate('/');
    }
  }, [user])
  
  useEffect(() => {
    const configData = connectors.find(c => c.id === configId) || null;
    if (configData) {
      setConfig(configData)
      setConfig(configData)
      setValue('name', configData.connectionName || '');
      setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
      setValue('accessKeyId', configData.authFields?.accessKeyId || '');
      setValue('secretAccessKey', configData.authFields?.secretAccessKey || '');
      setValue('bucketName', configData.authFields?.bucketName || '');
      setValue('region', configData.authFields?.region || '');
      setValue('sessionToken', configData.authFields?.sessionToken || '');
      setValue('endpointUrl', configData.config?.baseUrl || '');
      setValue('prefix', configData.authFields?.prefix || '');
      setValue('testPath', configData.config?.testPath || '');
    }
  }, [connectors, configId]);

  const onSubmit = async (data: S3Form) => {
    if (!data.environment) {
      setError('environment', { type: 'manual', message: 'Environment is required' });
      toast.error('Please select an environment');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        connectionName: data.name,
        environment: data.environment,
        config: {
          baseUrl: data.endpointUrl || '',
          testPath: data.testPath || '',
        },
        authFields: {
          accessKeyId: data.accessKeyId,
          secretAccessKey: data.secretAccessKey,
          bucketName: data.bucketName,
          region: data.region,
          sessionToken: data.sessionToken || '',
          prefix: data.prefix || '',
        },
        userId: user?.id!,
      };

      let response;
      if (!config) response = await configApi.post(payload);
      else response = await configApi.put(config.id, payload);

      setConfig(response);
      updateConnection(response);
      toast.success('Configuration saved successfully!');
    } catch (error: any) {
      toast.error(`Failed to save configuration: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!config?.id) {
      toast.error('Please save configuration first');
      return;
    }

    // Get current form values
    const formData = watch();
    
    const connectionData = {
      connectionName: formData.name,
      environment: formData.environment,
      config: {
        baseUrl: formData.endpointUrl || '',
        testPath: formData.testPath || '',
      },
      authFields: {
        accessKeyId: formData.accessKeyId,
        secretAccessKey: formData.secretAccessKey,
        bucketName: formData.bucketName,
        region: formData.region,
        sessionToken: formData.sessionToken || '',
        prefix: formData.prefix || '',
      },
    };

    await testConnectionApi(config.id, setTesting, connectionData, user);
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>S3 Connection</h1>
          <p>Configure your AWS S3 connection settings</p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={goBack}
            className={styles.cancelBtn}
          >
            Back
          </button>
          <button
            type="button"
            onClick={testConnection}
            disabled={testing || !config}
            className={styles.saveBtn}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            className={styles.saveBtn}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.fields}>
          <div className={styles.field}>
            <SecureInput
              fieldType="name"
              label="Connection Name"
              value={watch('name') || ''}
              onChange={(value) => setValue('name', value)}
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Environment</label>
            <select
              value={watch('environment') || ''}
              onChange={(e) => {setValue('environment', e.target.value as 'test' | 'prod' | 'dev'); 
                setError('environment', { type: 'manual', message: '' }); }}
              className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
            >
              <option value="">Select environment</option>
              <option value="prod">Prod</option>
              <option value="test">Test</option>
              <option value="dev">Dev</option>
            </select>
            {errors.environment && (
              <span className={styles.error}>{errors.environment.message}</span>
            )}
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="key"
              label="AWS Access Key ID *"
              icon={<Key size={16} />}
              value={watch('accessKeyId') || ''}
              onChange={(value) => setValue('accessKeyId', value)}
              placeholder="AKIA..."
              required
              className={styles.input}
              externalError={errors.accessKeyId?.message}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="password"
              label="AWS Secret Access Key *"
              icon={<Lock size={16} />}
              value={watch('secretAccessKey') || ''}
              onChange={(value) => setValue('secretAccessKey', value)}
              autoComplete="new-password"
              required
              skipPasswordValidation={true}
              className={styles.input}
              externalError={errors.secretAccessKey?.message}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="text"
              label="Bucket Name *"
              value={watch('bucketName') || ''}
              onChange={(value) => setValue('bucketName', value)}
              placeholder="my-app-uploads"
              required
              className={styles.input}
              externalError={errors.bucketName?.message}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="text"
              label="AWS Region *"
              value={watch('region') || ''}
              onChange={(value) => setValue('region', value)}
              placeholder="us-east-1"
              required
              className={styles.input}
              externalError={errors.region?.message}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="text"
              label="Session Token (optional)"
              value={watch('sessionToken') || ''}
              onChange={(value) => setValue('sessionToken', value)}
              placeholder="STS temporary token"
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="url"
              label="Endpoint URL (optional)"
              icon={<LinkIcon size={16} />}
              value={watch('endpointUrl') || ''}
              onChange={(value) => setValue('endpointUrl', value)}
              placeholder="https://s3.amazonaws.com"
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="text"
              label="Path/Prefix (optional)"
              value={watch('prefix') || ''}
              onChange={(value) => setValue('prefix', value)}
              placeholder="temp-uploads/"
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <SecureInput
              fieldType="text"
              label="Test Path (optional)"
              value={watch('testPath') || ''}
              onChange={(value) => setValue('testPath', value)}
              placeholder="Relative path for connection testing"
              className={styles.input}
            />
          </div>
        </div>
        <div className={styles.demoNotice}>
          Security tips: use IAM roles where possible, avoid hardcoding credentials, grant least-privilege (e.g., s3:PutObject), and consider presigned URLs for client uploads.
        </div>
      </form>
    </div>
  );
}
